import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Bell, Heart, ShoppingBag, User, LogOut, Store, Package, Wallet, ShieldCheck, Search, Ruler, ArrowLeftRight } from "lucide-react";
import CategoryName from "@/components/CategoryName";
import Avatar from "@/components/Avatar";
import { GARMENTS } from "@/lib/taxonomy";

export default function Layout({ children }) {
  const { user, logout, resendVerification } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [resent, setResent] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [offerCount, setOfferCount] = useState(0);
  const [brandCount, setBrandCount] = useState(0);
  const [searchCount, setSearchCount] = useState(0);
  const [tradeCount, setTradeCount] = useState(0);
  const [openMenu, setOpenMenu] = useState(null);
  const [search, setSearch] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    const term = search.trim();
    nav(term ? `/browse?q=${encodeURIComponent(term)}` : "/browse");
  };

  useEffect(() => { setOpenMenu(null); }, [loc.pathname, loc.search]);

  useEffect(() => {
    if (!openMenu) return;
    const close = (e) => {
      if (!e.target.closest("[data-nav-menu]")) setOpenMenu(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [openMenu]);

  useEffect(() => {
    if (!user) { setMatchCount(0); setOfferCount(0); setBrandCount(0); setSearchCount(0); setTradeCount(0); return; }
    let cancel = false;
    async function poll() {
      try {
        const [matchRes, offerRes, brandRes, searchRes, tradeRes] = await Promise.all([
          api.get("/notifications/matches"),
          api.get("/offers"),
          api.get("/notifications/brand-alerts"),
          api.get("/notifications/saved-searches").catch(() => ({ data: { count: 0 } })),
          api.get("/trades/unread-count").catch(() => ({ data: { count: 0 } })),
        ]);
        if (!cancel) {
          setMatchCount(matchRes.data.count || 0);
          setOfferCount((offerRes.data || []).length);
          setBrandCount(brandRes.data.count || 0);
          setSearchCount(searchRes.data.count || 0);
          setTradeCount(tradeRes.data.count || 0);
        }
      } catch {}
    }
    poll();
    const t = setInterval(poll, 20000);
    return () => { cancel = true; clearInterval(t); };
  }, [user, loc.pathname]);

  const alertCount = matchCount + offerCount + brandCount + searchCount;

  return (
    <div className="App grain min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F6F0]/85 hairline-b">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 lg:px-12 h-16">
          <Link to="/" data-testid="brand-link" className="flex items-baseline gap-2">
            <span className="font-display font-black text-2xl tracking-tighter">Your</span>
            <span className="font-display font-light text-2xl italic tracking-tight" style={{color:"#2563EB"}}>Classic Style</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-[0.72rem] uppercase tracking-[0.22em]">
            {[
              { key: "women", label: "Women", to: "/browse?product_type=apparel&dept=women", garments: GARMENTS.women },
              { key: "men", label: "Men", to: "/browse?product_type=apparel&dept=men", garments: GARMENTS.men },
              { key: "kids", label: "Kids", to: "/browse?product_type=apparel&dept=kids",
                kidgroups: [
                  { key: "girls", label: "Girls", note: "2T – 14" },
                  { key: "boys", label: "Boys", note: "2T – 14" },
                ] },
              { key: "shoes", label: "Shoes", to: "/browse?product_type=shoes",
                audiences: [
                  { key: "womens", label: "Women's", note: "Adult" },
                  { key: "mens", label: "Men's", note: "Adult" },
                  { key: "kids", label: "Kids", note: "Children" },
                  { key: "junior", label: "Junior", note: "Teen" },
                  { key: "baby", label: "Baby", note: "Infant & toddler" },
                ] },
              { key: "handbags", label: "Handbags", to: "/browse?product_type=handbags" },
              { key: "accessories", label: "Accessories", to: "/browse?product_type=wallets",
                children: [
                  { key: "jewelry", label: "Jewelry", to: "/browse?product_type=necklaces", note: "Necklaces & more" },
                  { key: "watches", label: "Watches", to: "/browse?product_type=watches", note: "Quartz & automatic" },
                  { key: "wallets", label: "Wallets", to: "/browse?product_type=wallets", note: "Leather goods" },
                  { key: "hats", label: "Hats", to: "/browse?product_type=hats", note: "Caps & brims" },
                  { key: "ties", label: "Ties", to: "/browse?product_type=ties", note: "Silk & wool" },
                ] },
            ].map((c) => {
              const open = openMenu === c.key;
              return (
                <div key={c.key} className="relative px-2 py-1" data-nav-menu>
                  <button
                    type="button"
                    data-testid={`nav-${c.key}`}
                    onClick={() => setOpenMenu(open ? null : c.key)}
                    className={`px-1 py-1 hover:text-black/60 transition-colors ${open ? "underline underline-offset-8" : ""}`}
                    aria-expanded={open}
                  >
                    {c.label}
                  </button>
                  {open && (
                    <div
                      className="absolute left-0 top-full mt-2 min-w-[220px] max-h-[76vh] overflow-auto bg-white hairline shadow-md z-50"
                      data-testid={`submenu-${c.key}`}
                    >
                      {c.kidgroups ? (
                        <>
                          {c.kidgroups.map((k) => (
                            <Link
                              key={k.key}
                              to={`${c.to}&kid_gender=${k.key}`}
                              data-testid={`submenu-${c.key}-${k.key}`}
                              onClick={() => setOpenMenu(null)}
                              className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 border-b border-black/10"
                            >
                              <span className="font-medium">{k.label}</span>
                              <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">{k.note}</span>
                            </Link>
                          ))}
                          <Link
                            to={c.to}
                            data-testid={`submenu-${c.key}-all`}
                            onClick={() => setOpenMenu(null)}
                            className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-neutral-500"
                          >
                            <span>View all kids</span>
                            <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">Clothing</span>
                          </Link>
                        </>
                      ) : c.garments ? (
                        <>
                          {c.garments.map((g) => (
                            <Link
                              key={g.key}
                              to={`${c.to}&garment_type=${g.key}`}
                              data-testid={`submenu-${c.key}-${g.key}`}
                              onClick={() => setOpenMenu(null)}
                              className="flex items-center px-4 py-2.5 hover:bg-neutral-50 border-b border-black/10"
                            >
                              <span className="font-medium">{g.label}</span>
                            </Link>
                          ))}
                          <Link
                            to={c.to}
                            data-testid={`submenu-${c.key}-all`}
                            onClick={() => setOpenMenu(null)}
                            className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-neutral-500"
                          >
                            <span>View all {c.label}</span>
                            <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">Clothing</span>
                          </Link>
                        </>
                      ) : c.audiences ? (
                        <>
                          {c.audiences.map((a) => (
                            <Link
                              key={a.key}
                              to={`${c.to}&audience=${a.key}`}
                              data-testid={`submenu-${c.key}-${a.key}`}
                              onClick={() => setOpenMenu(null)}
                              className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 border-b border-black/10"
                            >
                              <span className="font-medium">{a.label}</span>
                              <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">{a.note}</span>
                            </Link>
                          ))}
                          <Link
                            to={c.to}
                            data-testid={`submenu-${c.key}-all`}
                            onClick={() => setOpenMenu(null)}
                            className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-neutral-500"
                          >
                            <span>View all shoes</span>
                            <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">Everything</span>
                          </Link>
                        </>
                      ) : c.children ? (
                        <>
                          {c.children.map((s) => (
                            <Link
                              key={s.key}
                              to={s.to}
                              data-testid={`submenu-${c.key}-${s.key}`}
                              onClick={() => setOpenMenu(null)}
                              className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 border-b border-black/10"
                            >
                              <span className="font-medium">{s.label}</span>
                              <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">{s.note}</span>
                            </Link>
                          ))}
                          <Link
                            to="/browse"
                            data-testid={`submenu-${c.key}-all`}
                            onClick={() => setOpenMenu(null)}
                            className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-neutral-500"
                          >
                            <span>View all</span>
                            <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">Everything</span>
                          </Link>
                        </>
                      ) : (
                        <>
                          <Link
                            to={`${c.to}&condition=new`}
                            data-testid={`submenu-${c.key}-new`}
                            onClick={() => setOpenMenu(null)}
                            className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 border-b border-black/10"
                          >
                            <span className="font-medium" style={{color:"#2563EB"}}>New</span>
                            <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">Daily arrivals</span>
                          </Link>
                          <Link
                            to={`${c.to}&condition=used`}
                            data-testid={`submenu-${c.key}-classic`}
                            onClick={() => setOpenMenu(null)}
                            className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 border-b border-black/10"
                          >
                            <span className="font-medium">Classic</span>
                            <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">Pre-loved</span>
                          </Link>
                          <Link
                            to={c.to}
                            data-testid={`submenu-${c.key}-all`}
                            onClick={() => setOpenMenu(null)}
                            className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-neutral-500"
                          >
                            <span>View all</span>
                            <span className="text-[0.62rem] text-neutral-400 normal-case tracking-normal">Everything</span>
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <Link to="/archives" data-testid="nav-archives"
              className="px-3 py-1 normal-case tracking-[0.22em] hover:text-black/60 transition-colors">Fabrics</Link>
            <Link to="/browse?category=home_decor" data-testid="nav-home-decor"
              className="px-3 py-1 normal-case tracking-[0.22em] hover:text-black/60 transition-colors">Home Decor</Link>
            <Link to="/trade" data-testid="nav-trade"
              className="px-3 py-1 normal-case tracking-[0.22em] hover:text-black/60 transition-colors inline-flex items-center gap-1.5"
              style={{ color: "#1E7A3B" }}>
              <ArrowLeftRight size={13} strokeWidth={1.8} /> Trade
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden sm:flex items-center hairline bg-white/70 focus-within:bg-white transition-colors"
              data-testid="nav-search-form" role="search">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="nav-search-input"
                placeholder="Search items, brands…"
                className="bg-transparent px-3 py-1.5 text-sm outline-none w-32 lg:w-52"
                aria-label="Search the marketplace"
              />
              <button type="submit" data-testid="nav-search-btn" aria-label="Search"
                className="px-2.5 py-1.5 hover:bg-black/5 transition-colors">
                <Search size={16} strokeWidth={1.6} />
              </button>
            </form>
            <Link to="/browse" data-testid="nav-search-mobile" aria-label="Search"
              className="sm:hidden flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors">
              <Search size={18} strokeWidth={1.5} />
              <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Search</span>
            </Link>
            <Link to="/my-sizes" data-testid="nav-my-sizes" title="My sizes"
              className="flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors">
              <Ruler size={18} strokeWidth={1.5} />
              <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Sizes</span>
            </Link>
            {user ? (
              <>
                <Link to="/saved" data-testid="nav-saved" className="relative flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors">
                  <Heart size={18} strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Favorites</span>
                  {alertCount > 0 && (
                    <span data-testid="match-badge"
                      className="klein-pulse absolute -top-1 -right-1 h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-bold text-white"
                      style={{background:"#2563EB"}}>
                      {alertCount}
                    </span>
                  )}
                </Link>
                <Link to="/orders" data-testid="nav-orders" className="flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors" title="Purchases & protection">
                  <Package size={18} strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Orders</span>
                </Link>
                <Link to="/dashboard" data-testid="nav-dashboard" className="flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors">
                  <Store size={18} strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Selling</span>
                </Link>
                <Link to="/trades" data-testid="nav-trades" className="relative flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors" title="My trades">
                  <ArrowLeftRight size={18} strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Trades</span>
                  {tradeCount > 0 && <span data-testid="nav-trades-badge" className="absolute -top-0.5 right-0 min-w-[15px] h-[15px] px-1 rounded-full text-[9px] flex items-center justify-center text-white" style={{ background: "#2563EB" }}>{tradeCount}</span>}
                </Link>
                <Link to="/wallet" data-testid="nav-wallet" className="flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors" title="Wallet & how it works">
                  <Wallet size={18} strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Wallet</span>
                </Link>
                <Link to="/cart" data-testid="nav-cart" className="flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors">
                  <ShoppingBag size={18} strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Bag</span>
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors" title="Admin">
                    <ShieldCheck size={18} strokeWidth={1.5} />
                    <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Admin</span>
                  </Link>
                )}
                <Link to="/sell" data-testid="nav-sell" className="btn-primary text-[0.68rem]">Sell</Link>
                <Link to="/account" data-testid="nav-account" title="Account & handle" className="flex items-center gap-2 px-2 py-1 hover:bg-black/5 transition-colors">
                  <Avatar src={user.avatar} name={user.handle || user.name} size={26} />
                  <span className="hidden lg:flex flex-col leading-tight">
                    <span className="text-[11px] font-medium truncate max-w-[120px]" data-testid="nav-my-handle">{user.handle || user.name}</span>
                    <span className="text-[8px] tracking-[0.14em] uppercase text-neutral-400">Account</span>
                  </span>
                </Link>
                <button data-testid="nav-logout" onClick={async()=>{await logout(); nav("/");}} className="flex flex-col items-center px-2 py-1 hover:bg-black/5 transition-colors">
                  <LogOut size={18} strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Log out</span>
                </button>
              </>
            ) : user === false ? (
              <Link to="/auth" data-testid="nav-signin" className="btn-primary">Sign in</Link>
            ) : null}
          </div>
        </div>
      </header>
      {user && user.email_verified === false && (
        <div data-testid="verify-email-banner"
          className="w-full text-center text-sm px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap"
          style={{ background: "#FDF3D8", color: "#7A5B00", borderBottom: "1px solid #E9D9A6" }}>
          <span>Please verify your email to secure your account. We sent a confirmation link to <strong>{user.email}</strong>.</span>
          {resent ? (
            <span className="font-medium" data-testid="verify-resent-msg">Sent — check your inbox.</span>
          ) : (
            <button data-testid="verify-resend-btn"
              onClick={async () => { const ok = await resendVerification(); if (ok) setResent(true); }}
              className="underline font-medium hover:opacity-70 transition-opacity">Resend email</button>
          )}
        </div>
      )}
      <main className="flex-1">{children}</main>
      <footer className="hairline-t mt-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="font-display font-black text-xl">Your <span className="italic font-light" style={{color:"#2563EB"}}>Classic Style</span></div>
            <p className="mt-4 text-sm text-neutral-500 max-w-xs">A curated peer-to-peer marketplace for apparel, footwear and accessories — infant to adult.</p>
          </div>
          <div>
            <div className="label-eyebrow">Shop</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/browse?product_type=apparel&dept=women">Women</Link></li>
              <li><Link to="/browse?product_type=apparel&dept=men">Men</Link></li>
              <li><Link to="/browse?product_type=apparel&dept=kids">Kids</Link></li>
              <li><Link to="/browse?product_type=shoes">Shoes</Link></li>
              <li><Link to="/browse?product_type=handbags">Handbags</Link></li>
              <li><Link to="/brands">Heritage brands</Link></li>
              <li><Link to="/archives" data-testid="footer-archives-link"><CategoryName /></Link></li>
              <li><Link to="/browse?category=home_decor" data-testid="footer-home-decor-link">Home Decor</Link></li>
              <li><Link to="/trade" data-testid="footer-trade-link">Trade Market</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow">Sell</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/sell">Create a listing</Link></li>
              <li><Link to="/dashboard">Seller dashboard</Link></li>
              <li><Link to="/wallet" data-testid="footer-wallet-link">Wallet &amp; how it works</Link></li>
              <li><Link to="/proof-guide">Authenticity proof guide</Link></li>
              <li><Link to="/pricing">Fees &amp; seller plans</Link></li>
              <li><Link to="/orders">Purchases &amp; protection</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow">About</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/terms" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
              <li><Link to="/privacy" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/refunds" data-testid="footer-refunds-link">Refund &amp; Returns</Link></li>
              <li><Link to="/pricing">Fees &amp; seller plans</Link></li>
              <li><Link to="/proof-guide">Authenticity proof guide</Link></li>
            </ul>
            <p className="mt-4 text-sm text-neutral-500">© {new Date().getFullYear()} Your Classic Style. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
